import type {
    IRegDataStagedChange,
    IRegTemplateOption,
} from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { WasteReturnRegFieldKeys } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { REG_FIELD_KEYS_EXCLUDED_FROM_COMPARISON } from "../wasteReturnTemplateMappingDefaults";

export const RegDataMatchStatuses = {
    TemplateOnly: "TemplateOnly",
    ActiveMatch: "ActiveMatch",
    InactiveMatch: "InactiveMatch",
    DeletedMatch: "DeletedMatch",
} as const;

export const RegDataChangeTypes = {
    AddNewFromTemplate: "AddNewFromTemplate",
    ReactivateExisting: "ReactivateExisting",
    RestoreDeleted: "RestoreDeleted",
    SoftDeleteExisting: "SoftDeleteExisting",
    SyncFlagsFromTemplate: "SyncFlagsFromTemplate",
    SyncHazardousFromTemplate: "SyncHazardousFromTemplate",
    NoAction: "NoAction",
} as const;

export const RegDataWarningCodes = {
    CannotAddNewValue: "CannotAddNewValue",
    HazardousMismatch: "HazardousMismatch",
    DrFlagMismatch: "DrFlagMismatch",
    NoExtractedTemplateValues: "NoExtractedTemplateValues",
} as const;

export const GEOGRAPHY_TYPE_OPTIONS = ["District", "Region", "Sub-Region"] as const;
export type GeographyTypeOption = (typeof GEOGRAPHY_TYPE_OPTIONS)[number];

export const GeographyLifecycleMessages = {
    templateOnlyAdd:
        "This value is not in your reg data. Use the arrow to add it, then complete code, description, and type before preview.",
    stagedAddIncomplete: "Complete code, description, and type before previewing this geography add.",
} as const;

/** Show apply confirmation modal when pending staged changes exceed this count. */
export const BULK_APPLY_CONFIRM_THRESHOLD = 50;

export interface StagedChangesBreakdown {
    total: number;
    syncCount: number;
    softDeleteCount: number;
}

export function computeStagedChangesBreakdown(changes: IRegDataStagedChange[]): StagedChangesBreakdown {
    let syncCount = 0;
    let softDeleteCount = 0;
    for (const change of changes) {
        if (change.changeType === RegDataChangeTypes.SoftDeleteExisting) {
            softDeleteCount++;
        } else {
            syncCount++;
        }
    }
    return { total: changes.length, syncCount, softDeleteCount };
}

export function isGeographyAddEditedValueComplete(editedValue: Record<string, unknown> | null | undefined): boolean {
    const code = String(editedValue?.code ?? "").trim();
    const description = String(editedValue?.description ?? "").trim();
    const type = String(editedValue?.type ?? "").trim();
    return !!(code && description && type);
}

/** Bulk sync skips geography adds until code, description, and type are complete. */
export function isStagedChangeCompleteForBulkSync(change: IRegDataStagedChange): boolean {
    if (change.changeType !== RegDataChangeTypes.AddNewFromTemplate) {
        return true;
    }
    if (change.fieldKey !== WasteReturnRegFieldKeys.Geography) {
        return true;
    }
    return isGeographyAddEditedValueComplete(change.editedValue);
}

export function getStagedChangeKey(change: IRegDataStagedChange): string {
    const systemPart = change.systemRecordId ?? "";
    const templatePart = change.templateRawValue ?? "";
    return `${change.fieldKey}|${change.changeType}|${systemPart}|${templatePart}`;
}

export function countExtractedValuesInMapping(
    listSources: Record<string, { extractedValues?: unknown[] }> | undefined
): number {
    if (!listSources) return 0;
    return Object.entries(listSources).reduce((sum, [fieldKey, source]) => {
        if (REG_FIELD_KEYS_EXCLUDED_FROM_COMPARISON.includes(fieldKey)) return sum;
        return sum + (source.extractedValues?.length ?? 0);
    }, 0);
}

export function matchStatusToChangeType(matchStatus: string, _fieldKey: string): string | null {
    switch (matchStatus) {
        case RegDataMatchStatuses.TemplateOnly:
            return RegDataChangeTypes.AddNewFromTemplate;
        case RegDataMatchStatuses.InactiveMatch:
            return RegDataChangeTypes.ReactivateExisting;
        case RegDataMatchStatuses.DeletedMatch:
            return RegDataChangeTypes.RestoreDeleted;
        default:
            return null;
    }
}

export function buildInitialEditedValue(fieldKey: string, templateOption: IRegTemplateOption): Record<string, unknown> {
    const parsed = templateOption.parsed ?? {};
    switch (fieldKey) {
        case WasteReturnRegFieldKeys.Ewc:
            return {
                code: parsed.code ?? "",
                description: parsed.description ?? templateOption.rawValue,
                isHazardous: parsed.isHazardous ?? false,
            };
        case WasteReturnRegFieldKeys.Geography: {
            const templateText = String(
                (parsed.description && String(parsed.description).trim()) ||
                    (parsed.code && String(parsed.code).trim()) ||
                    templateOption.rawValue
            ).trim();
            const type = parsed.type != null ? String(parsed.type).trim() : "";
            if (!type) {
                return {
                    code: "",
                    description: templateText,
                    type: "",
                };
            }
            return {
                code: String(parsed.code ?? "").trim(),
                description: String(parsed.description ?? templateOption.rawValue).trim(),
                type,
            };
        }
        case WasteReturnRegFieldKeys.FacilityOperation:
            return {
                code: parsed.code ?? "",
                description: parsed.description ?? templateOption.rawValue,
            };
        case WasteReturnRegFieldKeys.TechnicalCompetence: {
            const schemeCode = String(
                (parsed.code && String(parsed.code).trim()) ||
                    (parsed.description && String(parsed.description).trim()) ||
                    templateOption.rawValue
            ).trim();
            return {
                code: schemeCode,
                description: "",
            };
        }
        case WasteReturnRegFieldKeys.EaDr:
            return {
                code: parsed.code ?? "",
                description: parsed.description ?? templateOption.rawValue,
                isForReceipt: parsed.isForReceipt ?? false,
                isForRemoval: parsed.isForRemoval ?? false,
            };
        default:
            return {
                code: parsed.code ?? "",
                description: parsed.description ?? templateOption.rawValue,
            };
    }
}

export function getStagedChangesForField(fieldKey: string, stagedChanges: IRegDataStagedChange[]): IRegDataStagedChange[] {
    return stagedChanges.filter((change) => change.fieldKey === fieldKey);
}

export function formatChangeTypeLabel(changeType: string): string {
    switch (changeType) {
        case RegDataChangeTypes.AddNewFromTemplate:
            return "Add from template";
        case RegDataChangeTypes.ReactivateExisting:
            return "Reactivate";
        case RegDataChangeTypes.RestoreDeleted:
            return "Restore";
        case RegDataChangeTypes.SoftDeleteExisting:
            return "Soft-delete";
        case RegDataChangeTypes.SyncFlagsFromTemplate:
            return "Sync membership flags";
        case RegDataChangeTypes.SyncHazardousFromTemplate:
            return "Sync hazardous flag";
        case RegDataChangeTypes.NoAction:
            return "Acknowledge mismatch";
        default:
            return changeType;
    }
}

export function hasHazardousMismatchWarning(warnings: { code: string }[]): boolean {
    return warnings.some((w) => w.code === RegDataWarningCodes.HazardousMismatch);
}

export function hasDrFlagMismatchWarning(warnings: { code: string }[]): boolean {
    return warnings.some((w) => w.code === RegDataWarningCodes.DrFlagMismatch);
}

export const ACTIONABLE_ACTIVE_MATCH_WARNING_CODES = [
    RegDataWarningCodes.DrFlagMismatch,
    RegDataWarningCodes.HazardousMismatch,
] as const;

export function hasActionableActiveMatchMismatch(warnings: { code: string }[]): boolean {
    return warnings.some((w) =>
        (ACTIONABLE_ACTIVE_MATCH_WARNING_CODES as readonly string[]).includes(w.code)
    );
}
